/**
 * AssetPlayer control icons — self-contained inline SVGs.
 *
 * Deliberately NOT imported from `@vidstack/react/icons`: that subpath is a
 * separate optimize-deps entry (hundreds of glyphs) whose first appearance
 * forces a vite dependency re-optimization + full reload mid-session, and
 * the same cold-start cost lands on every teammate/CI that pulls the change.
 * Inlining the dozen glyphs the transport actually uses keeps the package
 * self-contained and portable (the FR-008 "no library internals leak" intent
 * carries to its iconography too) and is CSP-safe — no script, no CDN, no
 * `dangerouslySetInnerHTML`.
 *
 * Each icon is a 24×24 `currentColor` SVG; size is controlled by the
 * consuming button's CSS (`.ap-btn svg`, `.ap-center-btn svg`), so no width/
 * height attribute is set here.
 */
import type { JSX } from 'react';

type IconProps = { className?: string };

function svg(path: JSX.Element, className?: string): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      {path}
    </svg>
  );
}

export function PlayIcon({ className }: IconProps): JSX.Element {
  return svg(<path d="M8 5v14l11-7z" />, className);
}

export function PauseIcon({ className }: IconProps): JSX.Element {
  return svg(<path d="M6 5h4v14H6zm8 0h4v14h-4z" />, className);
}

export function ReplayIcon({ className }: IconProps): JSX.Element {
  return svg(
    <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" />,
    className,
  );
}

export function MuteIcon({ className }: IconProps): JSX.Element {
  return svg(
    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3 3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4 9.91 6.09 12 8.18z" />,
    className,
  );
}

export function VolumeLowIcon({ className }: IconProps): JSX.Element {
  return svg(
    <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9z" />,
    className,
  );
}

export function VolumeHighIcon({ className }: IconProps): JSX.Element {
  return svg(
    <path d="M3 9v6h4l5 5V4L7 9zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />,
    className,
  );
}

export function FullscreenIcon({ className }: IconProps): JSX.Element {
  return svg(
    <path d="M7 14H5v5h5v-2H7zm-2-4h2V7h3V5H5zm12 7h-3v2h5v-5h-2zM14 5v2h3v3h2V5z" />,
    className,
  );
}

export function FullscreenExitIcon({ className }: IconProps): JSX.Element {
  return svg(
    <path d="M5 16h3v3h2v-5H5zm3-8H5v2h5V5H8zm6 11h2v-3h3v-2h-5zm2-11V5h-2v5h5V8z" />,
    className,
  );
}

export function PictureInPictureIcon({ className }: IconProps): JSX.Element {
  return svg(
    <path d="M19 7h-8v6h8zm-2 4h-4V9h4zm4-8H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16.01H3V4.98h18z" />,
    className,
  );
}

export function PictureInPictureExitIcon({ className }: IconProps): JSX.Element {
  return svg(
    <path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18zm-10-7h8v5h-8z" />,
    className,
  );
}

export function ClosedCaptionsIcon({ className }: IconProps): JSX.Element {
  return svg(
    <path d="M19 4H5c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H5V6h14zM7 11h3v1H7zm0 2h3v1H7zm7-2h3v1h-3zm0 2h3v1h-3z" />,
    className,
  );
}

export function ClosedCaptionsOnIcon({ className }: IconProps): JSX.Element {
  return svg(
    <path d="M19 4H5c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM11 11H9.5v-.5h-2v3h2V13H11v1c0 .55-.45 1-1 1H7c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1zm7 0h-1.5v-.5h-2v3h2V13H18v1c0 .55-.45 1-1 1h-3c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1z" />,
    className,
  );
}

export function SeekBackwardIcon({ className }: IconProps): JSX.Element {
  return svg(<path d="M11 18V6l-8.5 6zm.5-6 8.5 6V6z" />, className);
}

export function SeekForwardIcon({ className }: IconProps): JSX.Element {
  return svg(<path d="M4 18l8.5-6L4 6zm9-12v12l8.5-6z" />, className);
}
